import { useEffect, useState } from "react";
import { Icon } from "@/components/Icons";
import { firstName, shortDate, longDate } from "@/features/signals/format";
import {
  CapReachedError,
  ReopenConflictError,
  ReopenNotAllowedError,
  STAGE_ORDER,
  type MatchedContact,
  type Outcome,
  type PassReasonCode,
  type RecipientBrief,
  type RecipientHistoryEntry,
  type StageId,
} from "../types";
import type { RecipientActionMutations } from "../hooks";

/**
 * Recipient activation rail (Reskin Session C), sealed anatomy (rail order per
 * the 2026-07-16 sealed amendment, extended with the outreach zone): zone 4
 * activation context, the static RKG card (same as internal), zone 6 outreach —
 * the contacts-and-outreach home (parity amendment Item 3), zone 5 pursuit
 * ownership (owner + follow-up + notes + outcome), the zone 7 all-five pursuit
 * stage, and the paginated action history.
 *
 * Honest-data / ruling notes (flagged in the checkpoint):
 *   - RKG is a static, non-functional placeholder, identical to the internal
 *     modal — no graph data is wired on either tier.
 *   - Zone 6 is the recipient's contacts-and-outreach home: the LIVE Seamless
 *     "Find contacts outside the firm" search (moved from the left column) and
 *     the "Manage contacts in BDSE" link are functional. Draft Outreach remains
 *     available only after signing into BDSE. Matched contacts and find-results
 *     render inside the zone.
 *   - Pursuit stage recording uses record_stage_event where the recipient tier
 *     supports it (outreach_made / meeting / proposal). The next unreached stage
 *     bubble is itself the advance control (advance-only); the proposal→won
 *     transition is the Won outcome, not a stage event, so that bubble is inert.
 *   - Follow-up is wired to the recipient token event (follow_up_set). The
 *     recipient brief does not round-trip a stored due date on live, so the set
 *     date renders from local mutation state for the session (no cross-refresh
 *     claim); the durable record is the follow_up_set history entry.
 *   - Notes has no recipient data source, so it renders disabled with a 0 count.
 */
export function RecipientRail({
  brief,
  actions,
}: {
  brief: RecipientBrief;
  actions: RecipientActionMutations;
}): JSX.Element {
  return (
    <>
      <ActivationZone activation={brief.activation} />
      <RkgCard />
      <OutreachZone
        company={brief.signal.company}
        actions={actions}
        storedContacts={brief.foundContacts}
      />
      <PursuitOwnership brief={brief} actions={actions} />
      <PursuitStage reachedStage={brief.reachedStage} outcome={brief.outcome} actions={actions} />
      <ActionHistory entries={brief.history} />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Zone 4 · Activation context                                                */
/* -------------------------------------------------------------------------- */
function ActivationZone({ activation }: { activation: RecipientBrief["activation"] }): JSX.Element {
  return (
    <div className="sx-z-activation">
      <p className="sx-zone-title sx-sm">Activation context</p>
      <p className="sx-eyebrow">Relationship</p>
      <p className="sx-body sx-sm">{activation.relationship}</p>
      <p className="sx-eyebrow">Detail</p>
      <p className="sx-body sx-sm">{activation.detail}</p>
      <p className="sx-eyebrow">Monetization</p>
      <p className="sx-body sx-sm">{activation.monetization}</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* RKG card · static placeholder, identical to the internal modal              */
/* -------------------------------------------------------------------------- */
function RkgCard(): JSX.Element {
  return (
    <button type="button" className="sx-rkg" title="Relationship graph — coming soon">
      <svg
        viewBox="0 0 200 110"
        width="164"
        height="90"
        role="img"
        aria-label="Relational Knowledge Graph thumbnail"
      >
        <line x1="100" y1="54" x2="42" y2="28" stroke="rgba(7,60,70,0.25)" strokeWidth="1.2" />
        <line x1="100" y1="54" x2="158" y2="24" stroke="rgba(7,60,70,0.25)" strokeWidth="1.2" />
        <line x1="100" y1="54" x2="166" y2="72" stroke="rgba(7,60,70,0.25)" strokeWidth="1.2" />
        <line x1="100" y1="54" x2="56" y2="88" stroke="rgba(7,60,70,0.25)" strokeWidth="1.2" />
        <line x1="100" y1="54" x2="120" y2="96" stroke="rgba(7,60,70,0.25)" strokeWidth="1.2" />
        <line x1="42" y1="28" x2="158" y2="24" stroke="rgba(7,60,70,0.14)" strokeWidth="1" />
        <line x1="42" y1="28" x2="22" y2="60" stroke="rgba(7,60,70,0.14)" strokeWidth="1" />
        <line x1="22" y1="60" x2="56" y2="88" stroke="rgba(7,60,70,0.14)" strokeWidth="1" />
        <line x1="158" y1="24" x2="166" y2="72" stroke="rgba(7,60,70,0.14)" strokeWidth="1" />
        <line x1="166" y1="72" x2="120" y2="96" stroke="rgba(7,60,70,0.14)" strokeWidth="1" />
        <line x1="56" y1="88" x2="120" y2="96" stroke="rgba(7,60,70,0.14)" strokeWidth="1" />
        <circle className="sx-hub" cx="100" cy="54" r="12" fill="#3E8778" />
        <circle cx="42" cy="28" r="8" fill="#073C46" />
        <circle cx="158" cy="24" r="9" fill="#08575E" />
        <circle cx="166" cy="72" r="7" fill="#50D0C6" />
        <circle cx="56" cy="88" r="8" fill="#344B46" />
        <circle cx="120" cy="96" r="6" fill="#08575E" opacity="0.7" />
        <circle cx="22" cy="60" r="5" fill="#97B0AA" />
        <circle cx="100" cy="54" r="4.5" fill="#F5F2EA" />
      </svg>
      <span className="sx-zone-title sx-sm" style={{ display: "block", marginBottom: 1 }}>
        Relational Knowledge Graph
      </span>
      <span className="sx-helper" style={{ display: "block" }}>
        Relationship graph — coming soon
      </span>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* Zone 6 · Outreach — the contacts-and-outreach home (parity amendment Item 3) */
/* -------------------------------------------------------------------------- */
/**
 * The recipient's contacts-and-outreach home. Three actions in the sealed
 * sx-z-outreach materials:
 *   1. "Find contacts outside the firm" — the LIVE Seamless search (moved here
 *      from the left column's matched-contacts box); full searching / results /
 *      cap / empty state machine preserved.
 *   2. "Draft Outreach" — disabled-honest, later phase.
 *   3. "Manage contacts in BDSE" — the existing /contacts link (moved from the
 *      matched-contacts header).
 * Find-contacts search results render inside this zone (the searching / results /
 * cap / empty state machine is preserved). The "Your matched contacts" display
 * was removed (micro-punch 2026-07-16): matched contacts will surface via the
 * Relationship Check result payload in a later backend slice. Recipients cannot
 * claim, so the zone is ungated; the internal modal keeps its claim gate.
 */
function OutreachZone({
  company,
  actions,
  storedContacts,
}: {
  company: string;
  actions: RecipientActionMutations;
  storedContacts: MatchedContact[];
}): JSX.Element {
  const find = actions.findContacts;
  // Hydrate on open from the stored found contacts (get_magic_link_signal_brief,
  // no search call); a completed search supersedes them.
  const results = find.data ?? storedContacts;
  const [saved, setSaved] = useState<ReadonlySet<string>>(new Set());
  const save = (id: string) => setSaved((prev) => new Set(prev).add(id));

  return (
    <div className="sx-z-outreach">
      <p className="sx-eyebrow">Outreach actions</p>
      <div className="sx-outreach-list">
        <button
          type="button"
          className="sx-btn sx-btn-p2"
          disabled={find.isPending}
          onClick={() => find.mutate()}
        >
          <Icon name="search" />
          {find.isPending
            ? "Searching…"
            : results.length > 0
              ? "Refresh contact search"
              : "Find contacts outside the firm"}
        </button>
        <button type="button" className="sx-btn sx-btn-p2" disabled title="Coming soon">
          Draft Outreach
        </button>
        <a className="sx-btn sx-btn-p2" href="/contacts">
          <Icon name="address-book" />
          Manage contacts in BDSE
        </a>
      </div>

      <div className="sx-gate-note">
        <Icon name="clock" />
        <p className="sx-helper">Sign in to BDSE to create a signal-grounded outreach draft.</p>
      </div>

      {/* Stored found contacts hydrate on open (from brief.foundContacts); a
          search then refreshes them. The wrapper stays hidden only when nothing
          is stored AND no search has run — so the zone shows just the four
          actions until then, no empty bordered block. The "Your matched
          contacts" display was removed in the 2026-07-16 micro-punch. */}
      {find.isIdle && results.length === 0 ? null : (
        <div className="sxr-outreach-contacts">
          {find.isPending ? (
            <div className="sxr-find-status" role="status">
              <span className="sxr-spinner" aria-hidden="true" />
              Searching outside the firm for new contacts at {company}.
            </div>
          ) : find.isError ? (
            <div className="sxr-find-status sxr-find-msg--err" role="status">
              {find.error instanceof CapReachedError
                ? "Contact search limit reached for today."
                : "Contact search could not run right now. Try again shortly."}
            </div>
          ) : results.length > 0 ? (
            <div className="sxr-find-results">
              <p className="sxr-find-results-title">New contacts at {company}</p>
              {results.map((c) => (
                <ContactRow
                  key={c.id}
                  contact={c}
                  saved={saved.has(c.id)}
                  onSave={() => save(c.id)}
                />
              ))}
            </div>
          ) : find.isSuccess ? (
            <div className="sxr-find-status sxr-find-msg" role="status">
              No new contacts found for {company} right now.
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

/**
 * Contact row (moved from the dissolved MatchedContacts box). The match-strength
 * chip was suppressed (ruling 2026-07-16): find_contacts_proxy returns no
 * strength, so every live row read a constant synthetic "MODERATE" — a rating we
 * don't actually have. "Save to contacts" holds local optimistic state only (no
 * server persistence in this tier); "Draft" is disabled-honest, not a dead no-op.
 */
function ContactRow({
  contact,
  saved,
  onSave,
}: {
  contact: MatchedContact;
  saved?: boolean;
  onSave?: () => void;
}): JSX.Element {
  return (
    <div className="sxr-contact-row">
      <span className="sxr-avatar" aria-hidden="true">
        {initials(contact.name)}
      </span>
      <div className="sxr-contact-body">
        <div className="sxr-contact-name">{contact.name}</div>
        <div className="sxr-contact-title">{contact.title}</div>
      </div>
      {onSave ? (
        <button type="button" className="sx-btn sx-btn-p3" disabled={saved} onClick={onSave}>
          {saved ? "Saved" : "Save to contacts"}
        </button>
      ) : null}
      <button
        type="button"
        className="sx-btn sx-btn-p3 sx-btn-quiet"
        disabled
        title="Drafting — coming soon"
      >
        Draft to {firstName(contact.name)}
      </button>
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

/* -------------------------------------------------------------------------- */
/* Zone 7 · Pursuit stage (all five, no morphing verb)                         */
/* -------------------------------------------------------------------------- */
const STAGE_LABELS: Record<StageId, string> = {
  assigned: "Assigned",
  outreach_made: "Outreach made",
  meeting: "Meeting scheduled",
  proposal: "Proposal",
  won: "Won",
};

/** Next stage recordable via record_stage_event (proposal→won is the outcome). */
const NEXT_STAGE: Partial<Record<StageId, StageId>> = {
  assigned: "outreach_made",
  outreach_made: "meeting",
  meeting: "proposal",
};

/**
 * Stage event id → stage label, for honest correction-history phrasing. Still
 * required after the walk-back affordance was removed (R-1): historical
 * stage_regressed entries from the operator tier remain in the timeline.
 */
const STAGE_LABEL_BY_EVENT: Record<string, string> = {
  stage_outreach_made: STAGE_LABELS.outreach_made,
  stage_meeting_scheduled: STAGE_LABELS.meeting,
  stage_proposal: STAGE_LABELS.proposal,
};

function PursuitStage({
  reachedStage,
  outcome,
  actions,
}: {
  reachedStage: StageId;
  outcome: Outcome | null;
  actions: RecipientActionMutations;
}): JSX.Element {
  const terminal = outcome !== null;
  const won = outcome === "won";
  const reachedIndex = STAGE_ORDER.indexOf(reachedStage);
  const doneCount = won ? STAGE_ORDER.length : reachedIndex + 1;
  const nextIndex = terminal ? -1 : doneCount;

  const advanceTo = terminal ? undefined : NEXT_STAGE[reachedStage];
  const pending = actions.recordStage.isPending;

  const stageError = actions.recordStage.error;
  const stageConflict =
    actions.recordStage.isError &&
    /\b409\b/.test(stageError instanceof Error ? stageError.message : String(stageError ?? ""));

  // The next unreached stage bubble is itself the advance control (Item 3):
  // advance-only, only the immediate next transition; reached and beyond-next
  // stages are inert. It records the same transition the retired "Record next
  // step" button recorded.
  const advance = (to: StageId) => {
    if (pending) return;
    actions.recordStage.reset();
    actions.recordStage.mutate(to);
  };

  return (
    <div className="sx-z-stage">
      <p className="sx-zone-title sx-sm">Pursuit stage</p>
      <ul className="sx-steps">
        {STAGE_ORDER.map((stage, i) => {
          const cls = i < doneCount ? " sx-done" : i === nextIndex ? " sx-next" : "";
          const advanceHere = i === nextIndex ? advanceTo : undefined;
          // Instant press feedback (Item 3): the next pip fills + pulses the
          // moment it's clicked, before the server responds.
          const pipPending = advanceHere != null && pending;
          const pip = (
            <span className={`sx-pip${pipPending ? " sx-pip--pending" : ""}`}>
              {i < doneCount ? (
                <svg
                  width="7"
                  height="7"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              ) : null}
            </span>
          );
          const name = <span className="sx-s-name">{STAGE_LABELS[stage]}</span>;
          return (
            <li key={stage} className={`sx-step${cls}`}>
              {advanceHere != null ? (
                <button
                  type="button"
                  className="sxr-step-btn"
                  disabled={pending}
                  title={`Record: ${STAGE_LABELS[advanceHere]}`}
                  aria-label={`Record pursuit stage: ${STAGE_LABELS[advanceHere]}`}
                  onClick={() => advance(advanceHere)}
                >
                  {pip}
                  {name}
                </button>
              ) : (
                <>
                  {pip}
                  {name}
                </>
              )}
            </li>
          );
        })}
      </ul>

      <RecipientCorrectControl outcome={outcome} reopenMutation={actions.reopenPursuit} />

      <p
        className="sx-helper"
        role="status"
        style={
          actions.recordStage.isError && !stageConflict ? { color: "var(--gold-ink)" } : undefined
        }
      >
        {actions.recordStage.isError
          ? stageConflict
            ? "Already recorded — reopen the link to see the latest."
            : "Couldn't record that step — try again."
          : pending
            ? "Recording…"
            : terminal
              ? "This pursuit is closed."
              : advanceTo != null
                ? "Click the next stage to advance. Each step is recorded to the signal history."
                : "Advance when it happens. Each step is recorded to the signal history."}
      </p>
    </div>
  );
}

/**
 * Quiet "Correct" affordance beside the pursuit-stage zone. On a CLOSED
 * (won/passed) pursuit it offers "Reopen pursuit"
 * (record_magic_link_recipient_action, action=outcome_reopened). A link minted
 * before the reopen feature returns 403 → the honest "ask for a fresh link"
 * copy, never a generic error.
 *
 * There is no stage walk-back here (R-1 / R-P4-5): regression is operator-only
 * and the token bearer path 403s every stage_regressed, so the affordance was
 * removed rather than left to fail on click. On an ACTIVE pursuit this control
 * renders nothing. The stage bubbles stay advance-only. The internal modal keeps
 * its own reopen path (record_outcome) and is unchanged.
 */
function RecipientCorrectControl({
  outcome,
  reopenMutation,
}: {
  outcome: Outcome | null;
  reopenMutation: RecipientActionMutations["reopenPursuit"];
}): JSX.Element | null {
  const canReopen = outcome !== null;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target;
      if (!(t instanceof Element) || !t.closest(".sxr-correct")) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
      }
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!canReopen) return null;

  function close() {
    setOpen(false);
    reopenMutation.reset();
  }

  const submitReopen = () => {
    reopenMutation.mutate(undefined, { onSuccess: close });
  };

  const reopenError = reopenMutation.error;
  const reopenMessage =
    reopenError instanceof ReopenNotAllowedError
      ? "This link doesn't allow reopening — ask for a fresh link."
      : reopenError instanceof ReopenConflictError
        ? "This pursuit isn't closed anymore — reopen the link to see the latest."
        : "Couldn't reopen — try again.";

  return (
    <div className="sxr-correct" style={{ position: "relative", marginBottom: 8 }}>
      <button
        type="button"
        className="sx-btn sx-btn-p3 sx-btn-quiet"
        style={{ fontSize: 12 }}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
      >
        Correct
      </button>
      {open ? (
        <div className="sx-popover sx-popover--end" role="dialog" aria-label="Reopen pursuit">
          <div className="sx-popover-title">Reopen this pursuit?</div>
          <p className="sx-popover-help">
            Reopening returns the pursuit to active and clears the{" "}
            {outcome === "won" ? "Won" : "Passed"} outcome. The correction is recorded to the signal
            history.
          </p>
          {reopenMutation.isError ? (
            <p className="sx-popover-help sx-popover-help--error">{reopenMessage}</p>
          ) : null}
          <div className="sx-popover-actions">
            <button type="button" className="sx-btn sx-btn-p3" onClick={close}>
              Cancel
            </button>
            <button
              type="button"
              className="sx-cta"
              disabled={reopenMutation.isPending}
              onClick={submitReopen}
            >
              {reopenMutation.isPending ? "Reopening…" : "Reopen pursuit"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Zone 5 · Pursuit ownership (owner + follow-up + notes + outcome)            */
/* -------------------------------------------------------------------------- */
function PursuitOwnership({
  brief,
  actions,
}: {
  brief: RecipientBrief;
  actions: RecipientActionMutations;
}): JSX.Element {
  const terminal = brief.outcome !== null;
  const recordOutcome = actions.recordOutcome;
  const pending = recordOutcome.isPending;
  // Which outcome is awaiting confirmation (parity amendment Item 4). Clicking
  // Won/Passed opens the confirm; the mutation only fires on Confirm.
  const [confirm, setConfirm] = useState<Outcome | null>(null);
  const [passReasonCode, setPassReasonCode] = useState<PassReasonCode | "">("");
  const [passReasonNote, setPassReasonNote] = useState("");

  // Escape / outside-press dismissal, matching the internal modal's popover
  // pattern (capture phase). The Won/Passed buttons and the popover both live
  // inside .sxr-outcome-confirm, which is the safe zone.
  useEffect(() => {
    if (!confirm) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target;
      if (!(t instanceof Element) || !t.closest(".sxr-outcome-confirm")) {
        setConfirm(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setConfirm(null);
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [confirm]);

  const openConfirm = (outcome: Outcome) => {
    recordOutcome.reset();
    setPassReasonCode("");
    setPassReasonNote("");
    setConfirm(outcome);
  };
  const submit = () => {
    if (!confirm) return;
    const passReason =
      confirm === "passed" && passReasonCode
        ? {
            code: passReasonCode,
            note: passReasonCode === "other" ? passReasonNote.trim() : undefined,
          }
        : undefined;
    recordOutcome.mutate({ outcome: confirm, passReason }, { onSuccess: () => setConfirm(null) });
  };
  const passReasonComplete =
    confirm !== "passed" ||
    (passReasonCode !== "" && (passReasonCode !== "other" || passReasonNote.trim().length > 0));

  return (
    <div className="sx-z-pursuit">
      <p className="sx-zone-title sx-sm">Pursuit ownership</p>
      <p className="sx-eyebrow">Owner</p>
      <p className="sx-body sx-sm">
        <span className="sx-dot" />
        {brief.ownershipLabel}
      </p>

      <FollowUp dueAt={brief.followUpDueAt} mutation={actions.setFollowUp} />

      <div className="sx-outcome">
        <p className="sx-eyebrow" style={{ marginBottom: 6 }}>
          Mark outcome
        </p>
        {terminal ? (
          <span className={`sxr-outcome-badge sxr-outcome-badge--${brief.outcome}`}>
            {brief.outcome === "won" ? "WON" : "PASSED"}
          </span>
        ) : (
          <div className="sxr-outcome-confirm">
            <div className="sx-outcome-row">
              <button
                type="button"
                className="sx-btn sx-btn-p2"
                aria-haspopup="dialog"
                aria-expanded={confirm === "won"}
                disabled={pending}
                onClick={() => openConfirm("won")}
              >
                Won
              </button>
              <button
                type="button"
                className="sx-btn sx-btn-p3"
                aria-haspopup="dialog"
                aria-expanded={confirm === "passed"}
                disabled={pending}
                onClick={() => openConfirm("passed")}
              >
                Passed
              </button>
            </div>
            {confirm ? (
              <div
                className="sx-popover sx-popover--end"
                role="dialog"
                aria-label={`Confirm ${confirm === "won" ? "Won" : "Passed"} outcome`}
              >
                <div className="sx-popover-title">
                  Mark this pursuit as {confirm === "won" ? "Won" : "Passed"}?
                </div>
                <p className="sx-popover-help">Recording an outcome closes the pursuit.</p>
                {confirm === "passed" ? (
                  <>
                    <label className="sx-popover-label" htmlFor="recipient-pass-reason">
                      Why are you passing?
                    </label>
                    <select
                      id="recipient-pass-reason"
                      className="sx-field"
                      value={passReasonCode}
                      onChange={(event) =>
                        setPassReasonCode(event.target.value as PassReasonCode | "")
                      }
                    >
                      <option value="">Choose a reason…</option>
                      <option value="not_a_fit">Not a fit</option>
                      <option value="at_capacity">At capacity</option>
                      <option value="pto">Going on PTO</option>
                      <option value="other">Other</option>
                    </select>
                    {passReasonCode === "other" ? (
                      <textarea
                        className="sx-field"
                        value={passReasonNote}
                        maxLength={500}
                        placeholder="Add the reason for passing…"
                        aria-label="Other reason for passing"
                        onChange={(event) => setPassReasonNote(event.target.value)}
                      />
                    ) : null}
                  </>
                ) : null}
                {recordOutcome.isError ? (
                  <p className="sx-popover-help sx-popover-help--error">
                    Couldn&rsquo;t record — try again.
                  </p>
                ) : null}
                <div className="sx-popover-actions">
                  <button
                    type="button"
                    className="sx-btn sx-btn-p3"
                    onClick={() => setConfirm(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="sx-cta"
                    disabled={pending || !passReasonComplete}
                    onClick={submit}
                  >
                    {pending ? "Recording…" : `Mark ${confirm === "won" ? "Won" : "Passed"}`}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        )}
        <p className="sx-helper" style={{ marginTop: 7 }}>
          {terminal ? "Outcome recorded." : "Recording an outcome closes the pursuit."}
        </p>
      </div>
    </div>
  );
}

function FollowUp({
  dueAt,
  mutation,
}: {
  dueAt: string | null;
  mutation: RecipientActionMutations["setFollowUp"];
}): JSX.Element {
  const [date, setDate] = useState("");
  // The recipient brief does not round-trip a stored due date on live, so the
  // set date is held in local mutation state and rendered for the rest of the
  // session (Item 4) — a persistent in-session confirmation, NOT a cross-refresh
  // persistence claim. The durable record is the follow_up_set entry in the
  // action history below.
  const [localDueAt, setLocalDueAt] = useState<string | null>(null);
  const { isPending, isError, reset } = mutation;
  const todayStr = new Date().toISOString().slice(0, 10);
  const shownDueAt = localDueAt ?? dueAt;

  const submit = () => {
    if (!date) return;
    // Clear any prior mutation state before firing again, so a second attempt
    // that fails surfaces the error honestly instead of a stale confirmation
    // (mirrors ToolButton).
    reset();
    // Local noon so the chosen day survives the round-trip to a UTC ISO string.
    const iso = new Date(`${date}T12:00:00`).toISOString();
    mutation.mutate(iso, {
      onSuccess: () => {
        setLocalDueAt(iso);
        setDate("");
      },
    });
  };

  return (
    <>
      {shownDueAt ? (
        <div className="sx-followup">
          <p className="sx-eyebrow">Follow-up due</p>
          <p className="sx-fu-date">{longDate(shownDueAt)}</p>
        </div>
      ) : null}

      <p className="sx-eyebrow" style={{ marginBottom: 4 }}>
        {shownDueAt ? "Update follow-up" : "Set a follow-up"}
      </p>
      <div className="sx-date-row">
        <input
          className="sx-date-input"
          type="date"
          value={date}
          min={todayStr}
          aria-label="Follow-up date"
          onChange={(e) => setDate(e.target.value)}
        />
        <button
          type="button"
          className="sx-btn sx-btn-p2"
          style={{ padding: "6px 11px", fontSize: 12 }}
          disabled={!date || isPending}
          onClick={submit}
        >
          {isPending ? "Saving…" : shownDueAt ? "Update" : "Set"}
        </button>
        {/* Notes — no recipient data source; disabled-honest, count 0. */}
        <button
          type="button"
          className="sx-btn sx-btn-p2"
          style={{ padding: "6px 11px", fontSize: 12 }}
          disabled
          title="Notes — coming soon"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M4 5h16v11l-4 4H4z" />
            <path d="M20 16h-4v4" />
          </svg>
          Notes <span className="sx-count">0</span>
        </button>
      </div>
      {isError ? (
        <p className="sxr-fu-confirm sxr-fu-confirm--err" role="status">
          Couldn't record — try again
        </p>
      ) : localDueAt ? (
        <p className="sxr-fu-confirm" role="status">
          Follow-up set for {longDate(localDueAt)} · recorded to the signal history
        </p>
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Action history · sealed collapsed→3→show-more pagination                    */
/* -------------------------------------------------------------------------- */
const VERB_PHRASE: Record<string, string> = {
  claim: "Claimed the signal",
  magic_link_open: "Opened the Magic Link",
  share: "Created a Magic Link",
  relationship_check_run: "Ran a relationship check",
  tariff_scan_run: "Ran a Tariff Terrain scan",
  asset_sent: "Sent an asset",
  contact_search_run: "Ran a contact search",
  outcome_recorded: "Recorded the outcome",
  link_snoozed: "Snoozed the link",
  follow_up_set: "Set a follow-up",
  stage_outreach_made: "Recorded outreach made",
  stage_meeting_scheduled: "Recorded a meeting",
  stage_proposal: "Recorded a proposal",
  outcome_reopened: "Reopened the pursuit",
  contacts_matched: "Matched your contacts",
};

/** Honest phrasing for a history entry, including the correction events (Item 4). */
function recipientPhrase(entry: RecipientHistoryEntry): string {
  if (entry.verb === "stage_regressed") {
    const label = entry.detail ? STAGE_LABEL_BY_EVENT[entry.detail] : undefined;
    return label ? `Walked back to ${label}` : "Walked back a stage";
  }
  return VERB_PHRASE[entry.verb] ?? entry.verb;
}

const HIST_PAGE = 3;

function ActionHistory({ entries }: { entries: RecipientHistoryEntry[] }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(HIST_PAGE);

  const ordered = [...entries].reverse(); // newest first
  const shown = ordered.slice(0, visible);
  const remaining = Math.max(0, ordered.length - visible);

  const collapse = () => {
    setOpen(false);
    setVisible(HIST_PAGE);
  };

  return (
    <div className="sx-history" data-open={open}>
      <button
        type="button"
        className="sx-strip"
        aria-expanded={open}
        onClick={() => (open ? collapse() : setOpen(true))}
      >
        <span className="sx-s-label">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#5E7472"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
          </svg>
          Action history
        </span>
        {entries.length > 0 ? <span className="sx-count">{entries.length}</span> : null}
      </button>

      {open ? (
        <div className="sx-hist-panel">
          {ordered.length === 0 ? (
            <p className="sx-hist-empty">No actions yet.</p>
          ) : (
            <>
              <ol className="sx-hist-list">
                {shown.map((e) => (
                  <li key={e.id}>
                    <span className="sx-hist-what">{recipientPhrase(e)}</span>
                    <span className="sx-hist-when">
                      {historyActor(e.actorName)} · {shortDate(e.at).toUpperCase()}
                    </span>
                  </li>
                ))}
              </ol>
              <div className="sx-hist-foot">
                {remaining > 0 ? (
                  <button
                    type="button"
                    className="sx-btn sx-btn-p3"
                    onClick={() => setVisible((v) => v + HIST_PAGE)}
                  >
                    Show {Math.min(HIST_PAGE, remaining)} more
                  </button>
                ) : null}
                <button type="button" className="sx-btn sx-btn-p3 sx-btn-quiet" onClick={collapse}>
                  Collapse
                </button>
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

/** "David Whitfield" → "D. WHITFIELD"; single word → uppercased as-is. */
function historyActor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) return name.toUpperCase();
  if (parts.length === 1) return first.toUpperCase();
  const last = parts[parts.length - 1] ?? "";
  return `${first.charAt(0)}. ${last}`.toUpperCase();
}
